import { computed, makeObservable, observable } from 'mobx'
import BookkeepingManagerSearchViewModel from './BookkeepingManagerSearchViewModel'
import {
  FetchUserWorkspaceMemberships,
  GetCurrentUserWorkspaceMemberships,
} from 'iam/WorkspaceMembershipStore'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import { task } from 'mobx-task'
import { once } from 'lodash'
import {
  BookkeepingManagerColumnDetails,
  BookkeepingManagerTableColumns,
} from './BookkeepingManagerTableColumn'
import { TableSortOrder } from 'components/Table'
import BookkeepingManagerStore from 'admin/BookkeepingManagerStore'
import BookkeepingManagerDetailsViewModel from './components/BookkeepingManagerDetails/BookkeepingManagerDetailsViewModel'
import BookkeepingPendingAction from './BookkeepingPendingAction'
import { AlertFactory } from 'alerts/Alert'
import InventoryStore from 'admin/InventoryStore'
import JobStore from 'jobs/JobStore'
import { GetCurrentMember } from 'iam/SessionStore'

export default class BookkeepingManagerViewModel {
  /**
   * The instance of BookkeepingManagerSearchViewModel.
   */
  searcher = new BookkeepingManagerSearchViewModel(
    this.bookkeepingManagerStore,
    this.fetchWorkspaceMemberships,
    this.getWorkspaceMemberships,
    this.createFilterStorage('Bookkeeping'),
  )

  /**
   * The bookkeeping manager details view model.
   */
  detailsVM = new BookkeepingManagerDetailsViewModel(
    this.bookkeepingManagerStore,
    this.newAlert,
    this.closeNav,
    this.jobStore,
    this.inventoryStore,
    this.getCurrentMember,
  )

  /**
   * Columns to populate the bookkeeping pending actions table header and rows with.
   */
  @observable
  columns = BookkeepingManagerTableColumns

  /**
   * Ensure we only activate the searcher once, as we want to keep it around
   * for subsequent page views of bookkeeping pending actions.
   */
  private activateSearcher = once(() => this.searcher.activate())

  constructor(
    private bookkeepingManagerStore: BookkeepingManagerStore,
    private fetchWorkspaceMemberships: FetchUserWorkspaceMemberships,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private createFilterStorage: FilterStorageFactory,
    private closeNav: () => void,
    private newAlert: AlertFactory,
    private jobStore: JobStore,
    private inventoryStore: InventoryStore,
    private getCurrentMember: GetCurrentMember,
  ) {
    makeObservable(this)

    this.detailsVM.update$.subscribe(
      (bookkeepingPendingAction: BookkeepingPendingAction) => {
        this.searcher.actionFilter.loadOptions()
        this.searcher.removeBookkeepingPendingAction(
          bookkeepingPendingAction.id,
        )
      },
    )
  }

  /**
   * The filtered bookkeeping pending actions.
   */
  @computed
  get filteredItems() {
    return this.searcher.filteredItems
  }

  /**
   * Gets the sort order for the specified column.
   */
  getSortOrder(column: BookkeepingManagerColumnDetails): TableSortOrder {
    return this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   */
  setSortOrder(
    column: BookkeepingManagerColumnDetails,
    direction: TableSortOrder,
  ) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(direction)
  }

  /**
   * Selects bookkeeping pending actions.
   */
  selectBookkeepingPendingAction(
    bookkeepingPendingAction: BookkeepingPendingAction,
  ) {
    this.detailsVM.show(bookkeepingPendingAction)
  }

  /*
   * Activates vm.
   */
  activate = task.resolved(async () => {
    await this.activateSearcher()
  })

  /**
   * Deactivates vm.
   */
  desactivate() {}
}
